import {
	CartesianGrid,
	Line,
	ComposedChart,
	ReferenceArea,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { parseApiDateAsUtc } from "./SensorPlacementPanel";
import type { PlacementHistoryEntry } from "./usePlacementHistory";

type Props = {
	entries: PlacementHistoryEntry[];
	field: "tower" | "chamber";
};

type Segment = {
	from: number;
	to: number;
	height: number;
	extensionInMeters: number | null;
	extensionIsOpaque: boolean | null;
};

export function HeightHistoryChart({ entries, field }: Props) {
	const heightOf = (e: PlacementHistoryEntry) =>
		field === "tower" ? e.towerHeightInMeters : e.chamberHeightInMeters;

	const now = Date.now();
	const segmentsByKey = new Map<string, Segment>();
	for (const e of entries) {
		const height = heightOf(e);
		if (height == null || e.validFrom == null) {
			continue;
		}
		const from = parseApiDateAsUtc(e.validFrom).getTime();
		if (from > now) {
			continue;
		}
		const to = e.validTo ? parseApiDateAsUtc(e.validTo).getTime() : now;
		const extensionInMeters = field === "chamber" ? e.chamberExtensionInMeters ?? null : null;
		const totalHeight = height + (extensionInMeters ?? 0);
		const key = `${from}|${to}|${totalHeight}|${extensionInMeters ?? ""}|${e.chamberExtensionIsOpaque ?? ""}`;
		if (!segmentsByKey.has(key)) {
			segmentsByKey.set(key, {
				from,
				to,
				height: totalHeight,
				extensionInMeters,
				extensionIsOpaque: e.chamberExtensionIsOpaque ?? null,
			});
		}
	}
	const segments = Array.from(segmentsByKey.values()).sort((a, b) => a.from - b.from);

	if (segments.length === 0) {
		return <p className="text-xs text-gray-400">No height history to plot</p>;
	}

	const times = Array.from(new Set(segments.flatMap((s) => [s.from, s.to]))).sort((a, b) => a - b);

	const chartData = times.map((t) => {
		let seg: Segment | undefined;
		for (const s of [...segments].reverse()) {
			if (t >= s.from && t <= s.to) {
				seg = s;
				break;
			}
		}
		return { t, height: seg ? seg.height : null };
	});

	const extensionBands = segments.filter((s) => s.extensionInMeters != null && s.extensionInMeters > 0);

	const formatDate = (t: number) => new Date(t).toISOString().slice(0, 10);

	return (
		<div className="text-xs text-gray-500">
			<ResponsiveContainer width="100%" height={180}>
				<ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
					<CartesianGrid strokeDasharray="3 3" className="stroke-gray-200" />
					<XAxis
						dataKey="t"
						type="number"
						domain={["dataMin", "dataMax"]}
						tickFormatter={formatDate}
						fontSize={10}
						scale="time"
					/>
					<YAxis
						fontSize={10}
						width={36}
						label={{ value: "m", angle: -90, position: "insideLeft", fontSize: 10 }}
					/>
					<Tooltip
						labelFormatter={(t) => formatDate(t as number)}
						formatter={(value) => {
							const height = typeof value === "number" ? value : Number(value);
							return [Number.isFinite(height) ? `${height.toFixed(2)} m` : "—", "Height"];
						}}
					/>
					{extensionBands.map((band, i) => (
						<ReferenceArea
							key={`ext-${i}`}
							x1={band.from}
							x2={band.to}
							fill={band.extensionIsOpaque ? "#78716c" : "#93c5fd"}
							fillOpacity={0.15}
							strokeOpacity={0}
							label={{
								value: band.extensionIsOpaque ? "extension (opaque)" : "extension (clear)",
								fontSize: 9,
								position: "insideTop",
								fill: "#6b7280",
							}}
						/>
					))}
					<Line
						dataKey="height"
						type="stepAfter"
						stroke="#0891b2"
						dot={false}
						strokeWidth={1.5}
						connectNulls={false}
					/>
				</ComposedChart>
			</ResponsiveContainer>
		</div>
	);
}
